import type { Rarity } from '@/shared/types/rarity';
import { cn } from '@/shared/utils/cn';

export const PROGRESS_TO_RARITY_THRESHOLDS = {
  UNCOMMON: 70,
  RARE: 80,
  EPIC: 90,
  LEGENDARY: 95,
} as const;

export function getRarityFromProgress(
  progressInPercents: number,
): Rarity | null {
  const clampedProgress = Math.min(Math.max(progressInPercents, 0), 100);

  // 0-69: no rarity (null) - "Ok fit"
  if (clampedProgress < PROGRESS_TO_RARITY_THRESHOLDS.UNCOMMON) {
    return null;
  }
  // 70-79: UNCOMMON
  if (clampedProgress < PROGRESS_TO_RARITY_THRESHOLDS.RARE) {
    return 'UNCOMMON';
  }
  // 80-89: RARE
  if (clampedProgress < PROGRESS_TO_RARITY_THRESHOLDS.EPIC) {
    return 'RARE';
  }
  // 90-94: EPIC
  if (clampedProgress < PROGRESS_TO_RARITY_THRESHOLDS.LEGENDARY) {
    return 'EPIC';
  }
  // 95-100: LEGENDARY
  return 'LEGENDARY';
}

/**
 * Rarity hex colors (for use in SVG, canvas, etc.)
 */
export const rarityColors: Record<Rarity, string> = {
  COMMON: 'var(--color-warm-500)',
  UNCOMMON: 'var(--color-cardgreen-500)',
  RARE: 'var(--color-cardblue-300)',
  EPIC: 'var(--color-cardpink-500)',
  LEGENDARY: 'var(--color-pending-600)',
} as const;

/**
 * Dynamic text colors
 * https://tailwindcss.com/docs/detecting-classes-in-source-files#dynamic-class-names
 */
export const rarityTextColors: Record<Rarity, string> = {
  COMMON: 'text-warm-500',
  UNCOMMON: 'text-cardgreen-500',
  RARE: 'text-cardblue-500',
  EPIC: 'text-cardpink-500',
  LEGENDARY: 'text-pending-600',
};

/**
 * Rarity border styles for main element
 * Solid color borders
 */
export const rarityBorderStyles = {
  small: 'border-4 border-black',
  large: 'border-8 border-black',
};

/**
 * Rarity background color classes for card content
 */
export const rarityBackgrounds: Record<Rarity, string> = {
  COMMON: 'bg-warm-950/40',
  UNCOMMON: 'bg-cardgreen-850/40',
  RARE: 'bg-cardblue-950/40',
  EPIC: 'bg-cardpink-950/40',
  LEGENDARY: 'bg-yellow-950/40',
} as const;

export const premiumRarityBackgrounds: Record<Rarity, string> = {
  COMMON: 'bg-warm-500',
  UNCOMMON: 'bg-cardgreen-500',
  RARE: 'bg-cardblue-500',
  EPIC: 'bg-cardpink-500',
  LEGENDARY: 'bg-pending-600',
} as const;

/**
 * Neon glow classes for premium badges
 */
export const rarityNeonClasses: Record<Rarity, string> = {
  COMMON: 'neon neon-warm-500',
  UNCOMMON: 'neon neon-cardgreen-500',
  RARE: 'neon neon-cardblue-500',
  EPIC: 'neon neon-cardpink-500',
  LEGENDARY: 'neon neon-pending-600',
} as const;

export const rarityBackgroundGradient: Record<Rarity, string> = {
  COMMON: 'bg-linear-180 from-cardgrey-600 via-cardgrey-850 to-black/70',
  UNCOMMON:
    'bg-linear-180 from-cardgreen-550 via-cardgreen-950 via-cardgreen-900 to-black/70',
  RARE: 'bg-linear-180 from-cardblue-700 via-cardblue-800 via-cardblue-900 to-black/70',
  EPIC: 'bg-linear-180 from-cardpink-800 via-cardpink-950 via-cardpink-900 to-black/70',
  LEGENDARY:
    'bg-linear-180 from-cardpending-600 via-cardyellow-800 via-cardpending-600 to-black/70',
} as const;

export const rarityFitLabels: Record<Rarity, string> = {
  COMMON: 'Ok fit',
  UNCOMMON: 'Uncommon fit',
  RARE: 'Rare fit',
  EPIC: 'Epic fit',
  LEGENDARY: 'Legendary fit',
};

/**
 * Outer card styles: border + raised effect
 * Used by RarityCard.Outer
 */
export const rarityOuterClasses: Record<Rarity, string> = {
  COMMON: 'border-warm-500 raised raised-0.5 raised-warm-500',
  UNCOMMON: 'border-cardgreen-500 raised raised-0.5 raised-cardgreen-500',
  RARE: 'border-cardblue-500 raised raised-0.5 raised-cardblue-500',
  EPIC: 'border-cardpink-500 raised raised-0.5 raised-cardpink-500',
  LEGENDARY: 'border-pending-600 raised raised-0.5 raised-pending-600',
} as const;

/**
 * @deprecated Use rarityOuterClasses instead
 * Base rarity shadow styles applied to all rarities
 * Includes outline and raised effects
 */
export const rarityBaseShadows: Record<Rarity, string> = {
  COMMON:
    'outline-solid outline-2 outline-warm-500 m-0.5 raised raised-warm-500 mb-1',
  UNCOMMON:
    'outline-solid outline-2 outline-cardgreen-500 m-0.5 raised raised-cardgreen-500 mb-1',
  RARE: 'outline-solid outline-2 outline-cardblue-500 m-0.5 raised raised-cardblue-500 mb-1',
  EPIC: 'outline-solid outline-2 outline-cardpink-500 m-0.5 raised raised-cardpink-500 mb-1',
  LEGENDARY:
    'outline-solid outline-2 outline-pending-600 m-0.5 raised raised-pending-600 mb-1',
} as const;

/**
 * Additional glow shadow effects for EPIC and LEGENDARY rarities
 * Only applied when innerBorderSize is 'large'
 */
export const rarityGlowShadows: Partial<Record<Rarity, string>> = {
  EPIC: 'drop-shadow-[0_0_16px_var(--tw-drop-shadow-color)] drop-shadow-cardpink-500/60',
  LEGENDARY:
    'drop-shadow-[0_0_16px_var(--tw-drop-shadow-color)] drop-shadow-pending-600/60',
} as const;

/**
 * Complete rarity shadow styles (base + glow) for backward compatibility
 * @deprecated Use rarityBaseShadows and rarityGlowShadows separately
 */
export const rarityShadows: Record<Rarity, string> = {
  COMMON: rarityBaseShadows.COMMON,
  UNCOMMON: rarityBaseShadows.UNCOMMON,
  RARE: rarityBaseShadows.RARE,
  EPIC: `${rarityBaseShadows.EPIC} ${rarityGlowShadows.EPIC ?? ''}`,
  LEGENDARY: `${rarityBaseShadows.LEGENDARY} ${rarityGlowShadows.LEGENDARY ?? ''}`,
} as const;

/**
 * Get border and shadow classes for a rarity (useful for modal borders, etc.)
 *
 * @param rarity - The rarity level
 * @param options - Optional configuration
 * @param options.includeBorder - Whether to include border classes (default: true)
 */
export const getRarityBorderClasses = (
  rarity: Rarity,
  options: {
    includeBorder?: boolean;
  } = {},
) => {
  const { includeBorder = true } = options;

  const border = includeBorder ? rarityBorderStyles : undefined;
  const background = includeBorder ? rarityBackgrounds[rarity] : undefined;
  const shadow = rarityShadows[rarity];

  return cn(border, background, shadow);
};
